// src/pages/finance-portal/tax-documents/TaxDocuments.js

import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Typography, Paper, Button, TextField, Card,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    TablePagination, Chip, Alert, Skeleton, MenuItem,
    InputAdornment, Dialog, DialogTitle, DialogContent, DialogActions, CircularProgress,
} from '@mui/material';
import DescriptionIcon from '@mui/icons-material/Description';
import SearchIcon from '@mui/icons-material/Search';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../../../utils/axiosInstance';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const ACCENT = '#22c55e';

const STATUS_COLORS = {
    DRAFT: { bg: '#f1f5f9', color: '#64748b' },
    GENERATED: { bg: '#dbeafe', color: '#1e40af' },
    REVIEWED: { bg: '#fef3c7', color: '#92400e' },
    FILED: { bg: '#dcfce7', color: '#166534' },
    CORRECTED: { bg: '#fce7f3', color: '#9d174d' },
};

const DOC_TYPES = [
    { value: '', label: 'All Types' },
    { value: 'W-2', label: 'W-2' },
    { value: '1099-NEC', label: '1099-NEC' },
];

function formatCurrency(val) {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(val || 0);
}

function getStatusLabel(status) {
    if (!status) return '';
    return status.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

const headCellSx = {
    fontFamily, fontWeight: 700, fontSize: '0.75rem', color: '#64748b',
    textTransform: 'uppercase', borderBottom: '2px solid #e2e8f0',
};

const bodyCellSx = {
    fontFamily, fontSize: '0.85rem', color: '#1e293b', borderBottom: '1px solid #f1f5f9',
};

export default function TaxDocuments() {
    const navigate = useNavigate();
    const [documents, setDocuments] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(20);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);

    // Filters
    const [docType, setDocType] = useState('');
    const [taxYear, setTaxYear] = useState('');
    const [status, setStatus] = useState('');
    const [search, setSearch] = useState('');

    // Bulk generate dialogs
    const [bulkW2Open, setBulkW2Open] = useState(false);
    const [bulk1099Open, setBulk1099Open] = useState(false);
    const [bulkYear, setBulkYear] = useState(new Date().getFullYear());
    const [generating, setGenerating] = useState(false);

    const fetchDocuments = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const params = { page: page + 1, per_page: rowsPerPage };
            if (docType) params.document_type = docType;
            if (taxYear) params.tax_year = taxYear;
            if (status) params.status = status;
            if (search) params.search = search;
            const res = await axiosInstance.get('/api/finance/payroll/tax-documents', { params });
            if (res.data.success) {
                setDocuments(res.data.data || []);
                setTotal(res.data.total || 0);
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load tax documents');
        } finally {
            setIsLoading(false);
        }
    }, [page, rowsPerPage, docType, taxYear, status, search]);

    useEffect(() => { fetchDocuments(); }, [fetchDocuments]);

    const handleBulkGenerate = async (type) => {
        setGenerating(true);
        setError(null);
        try {
            const endpoint = type === 'W-2'
                ? '/api/finance/payroll/tax-documents/bulk-w2'
                : '/api/finance/payroll/tax-documents/bulk-1099';
            const res = await axiosInstance.post(endpoint, { tax_year: parseInt(bulkYear, 10) });
            if (res.data.success) {
                setSuccess(`Generated ${res.data.count || 0} ${type} documents`);
            }
            setBulkW2Open(false);
            setBulk1099Open(false);
            fetchDocuments();
        } catch (err) {
            setError(err.response?.data?.message || `Failed to generate ${type} documents`);
        } finally {
            setGenerating(false);
        }
    };

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc' }}>
            {/* Header */}
            <Paper elevation={0} sx={{
                background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                borderRadius: { xs: 0, md: '0 0 24px 24px' },
                px: { xs: 2.5, md: 4 }, py: { xs: 3, md: 4 }, mb: 3,
                position: 'relative', overflow: 'hidden',
            }}>
                <Box sx={{ position: 'absolute', top: -30, right: -30, width: 160, height: 160, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.06)' }} />
                <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{ p: 1.5, borderRadius: '14px', bgcolor: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <DescriptionIcon sx={{ fontSize: 28, color: '#fff' }} />
                        </Box>
                        <Box>
                            <Typography variant="h4" sx={{ fontFamily, fontWeight: 800, color: 'white', fontSize: { xs: '1.4rem', md: '1.85rem' } }}>Tax Documents</Typography>
                            <Typography sx={{ fontFamily, color: 'rgba(255,255,255,0.8)', fontSize: '0.9rem', mt: 0.25 }}>W-2s, 1099s, and tax forms</Typography>
                        </Box>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                            variant="outlined"
                            startIcon={<PlayArrowIcon />}
                            onClick={() => setBulkW2Open(true)}
                            sx={{
                                fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px',
                                borderColor: 'rgba(255,255,255,0.5)', color: 'white',
                                '&:hover': { borderColor: '#fff', bgcolor: 'rgba(255,255,255,0.1)' },
                            }}
                        >
                            Generate All W-2s
                        </Button>
                        <Button
                            variant="contained"
                            startIcon={<PlayArrowIcon />}
                            onClick={() => setBulk1099Open(true)}
                            sx={{
                                fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px',
                                bgcolor: 'white', color: ACCENT,
                                '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' },
                            }}
                        >
                            Generate All 1099s
                        </Button>
                    </Box>
                </Box>
            </Paper>

            <Box sx={{ px: { xs: 2, md: 4 }, pb: 4, maxWidth: 1400, mx: 'auto' }}>
                {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2, borderRadius: '12px', fontFamily }}>{error}</Alert>}
                {success && <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2, borderRadius: '12px', fontFamily }}>{success}</Alert>}

                {/* Filters */}
                <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                    <TextField
                        size="small" placeholder="Search..."
                        value={search} onChange={e => { setSearch(e.target.value); setPage(0); }}
                        InputProps={{
                            startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: '#94a3b8' }} /></InputAdornment>,
                            sx: { fontFamily, borderRadius: '10px' },
                        }}
                        sx={{ minWidth: { xs: '100%', sm: 220 } }}
                    />
                    <TextField
                        size="small" select label="Type" value={docType}
                        onChange={e => { setDocType(e.target.value); setPage(0); }}
                        InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                        InputLabelProps={{ sx: { fontFamily } }}
                        sx={{ minWidth: 140 }}
                    >
                        {DOC_TYPES.map(t => <MenuItem key={t.value} value={t.value} sx={{ fontFamily }}>{t.label}</MenuItem>)}
                    </TextField>
                    <TextField
                        size="small" label="Tax Year" type="number" value={taxYear}
                        onChange={e => { setTaxYear(e.target.value); setPage(0); }}
                        InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                        InputLabelProps={{ shrink: true, sx: { fontFamily } }}
                        sx={{ minWidth: 120 }}
                    />
                    <TextField
                        size="small" select label="Status" value={status}
                        onChange={e => { setStatus(e.target.value); setPage(0); }}
                        InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                        InputLabelProps={{ sx: { fontFamily } }}
                        sx={{ minWidth: 140 }}
                    >
                        <MenuItem value="" sx={{ fontFamily }}>All</MenuItem>
                        {Object.keys(STATUS_COLORS).map(s => (
                            <MenuItem key={s} value={s} sx={{ fontFamily }}>{getStatusLabel(s)}</MenuItem>
                        ))}
                    </TextField>
                </Box>

                {/* Table */}
                <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                    <TableContainer sx={{ overflowX: 'auto' }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                    {['Type', 'Tax Year', 'Employee / Contractor', 'Wages / Compensation', 'Tax Withheld', 'Status', 'Actions'].map(h => (
                                        <TableCell key={h} sx={headCellSx}>{h}</TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {isLoading ? (
                                    [1, 2, 3, 4, 5].map(i => (
                                        <TableRow key={i}>{[1, 2, 3, 4, 5, 6, 7].map(j => <TableCell key={j}><Skeleton height={36} /></TableCell>)}</TableRow>
                                    ))
                                ) : documents.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={7} sx={{ textAlign: 'center', py: 6 }}>
                                            <Typography sx={{ fontFamily, color: '#94a3b8' }}>No tax documents found</Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : documents.map(doc => (
                                    <TableRow key={doc.id} sx={{ '&:hover': { bgcolor: '#f8fafc' }, cursor: 'pointer' }}
                                        onClick={() => navigate(`/finance-portal/tax-documents/${doc.id}`)}>
                                        <TableCell sx={{ ...bodyCellSx, fontWeight: 600 }}>
                                            <Chip label={doc.document_type} size="small"
                                                sx={{ fontFamily, fontWeight: 600, fontSize: '0.72rem', bgcolor: doc.document_type === 'W-2' ? '#dbeafe' : '#fef3c7', color: doc.document_type === 'W-2' ? '#1e40af' : '#92400e' }} />
                                        </TableCell>
                                        <TableCell sx={bodyCellSx}>{doc.tax_year}</TableCell>
                                        <TableCell sx={bodyCellSx}>{doc.employee_name || doc.contractor_name || '—'}</TableCell>
                                        <TableCell sx={{ ...bodyCellSx, fontWeight: 600 }}>
                                            {formatCurrency(doc.document_type === 'W-2' ? doc.box1_wages : doc.box1_nonemployee_compensation)}
                                        </TableCell>
                                        <TableCell sx={bodyCellSx}>
                                            {formatCurrency(doc.document_type === 'W-2' ? doc.box2_federal_tax : 0)}
                                        </TableCell>
                                        <TableCell sx={bodyCellSx}>
                                            <Chip label={getStatusLabel(doc.status)} size="small"
                                                sx={{
                                                    fontFamily, fontWeight: 600, fontSize: '0.72rem',
                                                    bgcolor: (STATUS_COLORS[doc.status] || STATUS_COLORS.DRAFT).bg,
                                                    color: (STATUS_COLORS[doc.status] || STATUS_COLORS.DRAFT).color,
                                                }} />
                                        </TableCell>
                                        <TableCell sx={bodyCellSx}>
                                            <Button size="small" sx={{ fontFamily, textTransform: 'none', fontSize: '0.8rem', color: ACCENT }}
                                                onClick={(e) => { e.stopPropagation(); navigate(`/finance-portal/tax-documents/${doc.id}`); }}>
                                                View
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        component="div" count={total} page={page}
                        onPageChange={(_, p) => setPage(p)}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                        rowsPerPageOptions={[10, 20, 50]}
                        sx={{ '& *': { fontFamily } }}
                    />
                </Card>
            </Box>

            {/* Bulk W-2 Dialog */}
            <Dialog open={bulkW2Open} onClose={() => setBulkW2Open(false)} PaperProps={{ sx: { borderRadius: '16px' } }}>
                <DialogTitle sx={{ fontFamily, fontWeight: 700 }}>Generate All W-2s</DialogTitle>
                <DialogContent>
                    <Typography sx={{ fontFamily, fontSize: '0.9rem', color: '#64748b', mb: 2 }}>
                        Generate W-2 forms for all employees for the selected tax year.
                    </Typography>
                    <TextField
                        size="small" fullWidth label="Tax Year" type="number"
                        value={bulkYear} onChange={e => setBulkYear(e.target.value)}
                        InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                        InputLabelProps={{ sx: { fontFamily } }}
                    />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setBulkW2Open(false)} sx={{ fontFamily, textTransform: 'none', color: '#64748b' }}>Cancel</Button>
                    <Button onClick={() => handleBulkGenerate('W-2')} variant="contained" disabled={generating}
                        sx={{ fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px', bgcolor: ACCENT, '&:hover': { bgcolor: '#16a34a' } }}>
                        {generating ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Generate'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Bulk 1099 Dialog */}
            <Dialog open={bulk1099Open} onClose={() => setBulk1099Open(false)} PaperProps={{ sx: { borderRadius: '16px' } }}>
                <DialogTitle sx={{ fontFamily, fontWeight: 700 }}>Generate All 1099-NECs</DialogTitle>
                <DialogContent>
                    <Typography sx={{ fontFamily, fontSize: '0.9rem', color: '#64748b', mb: 2 }}>
                        Generate 1099-NEC forms for all contractors paid $600+ for the selected tax year.
                    </Typography>
                    <TextField
                        size="small" fullWidth label="Tax Year" type="number"
                        value={bulkYear} onChange={e => setBulkYear(e.target.value)}
                        InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                        InputLabelProps={{ sx: { fontFamily } }}
                    />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setBulk1099Open(false)} sx={{ fontFamily, textTransform: 'none', color: '#64748b' }}>Cancel</Button>
                    <Button onClick={() => handleBulkGenerate('1099-NEC')} variant="contained" disabled={generating}
                        sx={{ fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px', bgcolor: ACCENT, '&:hover': { bgcolor: '#16a34a' } }}>
                        {generating ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Generate'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
